import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Spin,
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { fetchAllBranches, fetchBranchMembers } from "../../../apis/branch.api";
import { fetchClientDetails } from "../../../apis/client.api";
import {
  cancelPinelabTransaction,
  cancelRazorpayPosTransaction,
  checkAndConfirmOrderPayment,
  createEstimateTransaction,
  createPinelabTransaction,
  createRazorpayPosTransaction,
  editPI,
  fetchPIfromId,
  getEstimateTransactions,
  getEzetapBranchDetails,
  getEzetapEnabledBranchIds,
  getPinelabsDetails,
  getPinelabTransaction,
  getRazorpayPosTransaction,
  addCashToWallet,
  settleWalletTransactions,
} from "../../../apis/pro_forma_invoice.api";
import { editUser } from "../../../apis/user.api";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
import { HOLD_STOCK_ALLOWED_FROM } from "../pro_forma_invoice_details/pfi_order_details/holdStockAllowedFrom";

export interface OrderPlacedDetailsDrawerProps {
  onFinish: (values: any) => void;
  onCancel: () => void;
  estimateId: number;
  isAddCashToWallet?: boolean;
  customPendingAmount?: number;
}

export const PaymentTypes = (hasCreditPermission: boolean = false) => [
  { value: "cash", label: "Cash", referenceTypes: ["others"] },
  ...(hasCreditPermission
    ? [{ value: "credit", label: "Credit", referenceTypes: ["others"] }]
    : []
  ),
  {
    value: "neft_rtgs_imps",
    label: "NEFT / RTGS / IMPS",
    referenceTypes: ["utr"],
  },
  { value: "upi", label: "UPI", referenceTypes: ["upi_transaction_id"] },
  { value: "cheque", label: "Cheque", referenceTypes: ["others"] },
  { value: "razorpay", label: "Razorpay", referenceTypes: ["others"] },
  { value: "pos", label: "POS (Pinelabs)", referenceTypes: ["others"] },
  { value: "razorpay-pos", label: "Razorpay POS", referenceTypes: ["others"] },
  { value: "pos-manual", label: "POS Manual", referenceTypes: ["others"] },
];

export const PaymentReferenceTypes = [
  { value: "utr", label: "UTR" },
  { value: "upi_transaction_id", label: "UPI Transaction ID" },
  { value: "others", label: "Others" },
];

const POSPaymentModes = [
  {
    value: "upi",
    label: "UPI",
  },
  {
    value: "card",
    label: "Card",
  },
  {
    value: "bank_emi",
    label: "Bank EMI",
  },
  {
    value: "brand_emi",
    label: "Brand EMI",
  },
];

const RazorpayPOSPaymentModes = [
  { value: "card", label: "Card" },
  { value: "upi", label: "UPI" },
  { value: "bharatqr", label: "BharatQR" },
  { value: "all", label: "All Modes" },
];

export const PaymentMediumOptions = [
  { value: "pinelabs", label: "PineLabs" },
  { value: "razorpay_pos", label: "Razorpay POS" },
];

export const PaymentModeOptions = [
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "wallet", label: "Wallet" },
  { value: "net_banking", label: "Net Banking" },
  { value: "emi", label: "EMI" },
];

let ezetapBranchIdsCache: Promise<number[]> | null = null;

export function OrderPlacedDetailsDrawer({
  onFinish,
  onCancel,
  estimateId,
  isAddCashToWallet,
  customPendingAmount,
}: OrderPlacedDetailsDrawerProps) {
  const [form] = Form.useForm();
  const [paymentType, setPaymentType] = useState<string | undefined>(undefined);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [estimateDetails, setEstimateDetails] = useState<any>(null);
  const [posPaymentMode, setPosPaymentMode] = useState<string>();
  const [paymentMedium, setPaymentMedium] = useState<string | undefined>(
    undefined
  );
  const [branchEzetapValid, setBranchEzetapValid] = useState<boolean | null>(
    null
  );
  const [ezetapBranchIds, setEzetapBranchIds] = useState<number[] | null>(null);
  const [checkingBranchEzetap, setCheckingBranchEzetap] = useState(false);
  const [branchMembers, setBranchMembers] = useState<any[]>([]);
  const [allbranchMembers, setAllBranchMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pineLabTransactionId, setPineLabTransactionId] = useState<
    number | null
  >(null);
  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const intervalId = useRef<NodeJS.Timeout | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [branchList, setBranchList] = useState<any[]>([]);
  const [currentAmount, setCurrentAmount] = useState<number>(0);
  const [pinelabsDetails, setPinelabsDetails] = useState<any>(null);
  const [ezetapDevices, setEzetapDevices] = useState<any>(null);
  const [razorpayPosTransactionId, setRazorpayPosTransactionId] = useState<number | null>(null);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (isAddCashToWallet) {
      setPaymentType("cash");
      form.setFieldsValue({ payment_type: "cash" });
    }
  }, [isAddCashToWallet, form]);

  useEffect(() => {
    (async () => {
      const clientId = estimateDetails?.client?.id ?? estimateDetails?.client;
      if (!clientId) return;
      if (currentAmount > 2_00_000 && paymentType === "cash") {
        const clientDetails = await fetchClientDetails(clientId);
        setTimeout(() => {
          form.setFieldsValue({
            pan_number: clientDetails?.pan,
          });
        }, 700);
      }
    })();
  }, [currentAmount, estimateDetails, form]);

  const onPosCancel = async () => {
    if (razorpayPosTransactionId) {
      const txn = await cancelRazorpayPosTransaction(razorpayPosTransactionId);
      if (txn) {
        setRazorpayPosTransactionId(null);
        message.success("Transaction cancelled successfully");
      } else {
        message.error("Failed to cancel transaction");
      }
    } else if (pineLabTransactionId) {
      const txn = await cancelPinelabTransaction(pineLabTransactionId);
      if (txn) {
        setPineLabTransactionId(null);
        message.success("Transaction cancelled successfully");
      } else {
        message.error("Failed to cancel transaction");
      }
    }

    setLoading(false);
    form.resetFields();
    setPaymentType(undefined);
    setPosPaymentMode(undefined);
    onCancel();
    clearInterval(intervalId.current as NodeJS.Timeout);
  };

  useEffect(() => {
    fetchBranchMembers().then((res) => {
      setAllBranchMembers(res)
    })
    const activeTransactionId = pineLabTransactionId || razorpayPosTransactionId;
    const isRazorpayPos = !!razorpayPosTransactionId;
    if (!activeTransactionId) return;
    if (intervalId.current) {
      clearInterval(intervalId.current);
    }
    intervalId.current = setInterval(async () => {
      const txn = isRazorpayPos
        ? await getRazorpayPosTransaction(activeTransactionId)
        : await getPinelabTransaction(activeTransactionId);
      if (txn?.status === "captured") {
        setLoading(false);
        message.success("Payment successful");
        if (isRazorpayPos) {
          setRazorpayPosTransactionId(null);
        } else {
          setPineLabTransactionId(null);
        }
        form.resetFields();

        const newTxns = await getEstimateTransactions(estimateId);

        const prevTxns = transactions;
        setTransactions(newTxns);
        maybePromptHoldStock(prevTxns, newTxns, { payment_type: isRazorpayPos ? "razorpay-pos" : "pos" });
      } else if (txn?.status === "pending") {
        // Do nothing
      } else if (txn?.status === "failed") {
        setLoading(false);
        message.error("Payment failed");
        if (isRazorpayPos) {
          setRazorpayPosTransactionId(null);
        } else {
          setPineLabTransactionId(null);
        }
        form.resetFields();
      }
    }, 4000);

    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
  }, [estimateId, form, onFinish, pineLabTransactionId, razorpayPosTransactionId]);

  const onSubmit = async (values: any) => {
    if (!hasPermission("pi.mark_as_order_placed")) {
      message.error("You don't have permission to place this order");
      return;
    }
    if (values?.payment_type === "pos") {
      // Pinelabs POS
      const payload = {
        estimate_id: estimateId,
        payment_method: values?.pos_payment_mode,
        amount: values?.payment_amount,
        machine_id: values?.pinelab_machine_name,
      };

      if (!payload?.payment_method) {
        message.error("Please select a payment method");
        return;
      }
      if (!payload?.amount) {
        message.error("Please enter a payment amount");
        return;
      }
      if (
        !["upi", "card", "bank_emi", "brand_emi"].includes(
          payload?.payment_method
        )
      ) {
        message.error("Please select a valid payment method");
        return;
      }

      if (pinelabsDetails && !payload?.machine_id) {
        message.error("Please select a machine name");
        return;
      }

      try {
        const transaction = await createPinelabTransaction({
          ...payload,
        });
        setPineLabTransactionId(transaction?.id);
        setLoading(true);
        message.success("Pinelab transaction created successfully");
      } catch (error) {
        console.error("Error creating Pinelab transaction:", error);
        message.error("Failed to create Pinelab transaction");
        return;
      }

      return;
    }

    if (values?.payment_type === "razorpay-pos") {
      // Razorpay POS (Ezetap)
      const payload: any = {
        estimate_id: estimateId,
        payment_method: values?.razorpay_pos_payment_mode,
        amount: String(values?.payment_amount),
        device_id: values?.ezetap_device,
        branch_id: values?.organisation_branch_id,
      };

      if (!payload?.payment_method) {
        message.error("Please select a payment method");
        return;
      }
      if (!payload?.amount || payload.amount === "undefined") {
        message.error("Please enter a payment amount");
        return;
      }
      if (ezetapDevices?.length && !payload?.device_id) {
        message.error("Please select a Razorpay POS device");
        return;
      }

      try {
        const transaction = await createRazorpayPosTransaction(payload);
        if (!transaction) return;
        setRazorpayPosTransactionId(transaction?.id);
        setLoading(true);
        message.success("Razorpay POS transaction created successfully");
      } catch (error) {
        console.error("Error creating Razorpay POS transaction:", error);
        message.error("Failed to create Razorpay POS transaction");
        return;
      }

      return;
    }

    if (isAddCashToWallet) {
      setSubmitting(true);
      if (currentAmount > 2_00_000 && values?.payment_type === "cash") {
        if (!values?.pan_number) {
          message.error("Please enter the PAN number");
          setSubmitting(false);
          return;
        }
        await editUser(
          { pan: values?.pan_number },
          estimateDetails?.client ?? estimateDetails?.client?.id
        );
      }

      const res = await addCashToWallet({
        client_id: estimateDetails?.client?.id ?? estimateDetails?.client,
        source: "CASH",
        amount: currentAmount * 100,
        payment_method: "cash",
        payment_date: new Date().toISOString(),
        collected_by_id: values?.wallet_collected_by ?? undefined,
      });

      if (res) {
        message.success("Cash added to wallet successfully");
        form.resetFields();
        setPaymentType(undefined);
        onFinish(values);
      }
      setSubmitting(false);
      return;
    }

    if (["razorpay", "pos-manual"].includes(values?.payment_type) && !values?.payment_mode) {
      message.error("Please select a payment mode");
      return;
    }

    if (values?.payment_type === "pos-manual" && !values?.payment_medium) {
      message.error("Please select a payment medium");
      return;
    }

    if (
      values?.payment_type === "pos-manual" &&
      values?.payment_medium === "razorpay_pos"
    ) {
      if (!values?.organisation_branch_id) {
        message.error("Please select a branch");
        return;
      }
      if (checkingBranchEzetap) {
        message.error("Please wait, verifying the selected branch");
        return;
      }
      let isValid = branchEzetapValid;
      if (isValid === null) {
        if (ezetapBranchIdsCache) {
          isValid = (await ezetapBranchIdsCache).includes(
            values?.organisation_branch_id
          );
        } else {
          const res = await getEzetapBranchDetails(
            values?.organisation_branch_id
          );
          isValid = Array.isArray(res) ? res.length > 0 : Boolean(res?.id);
        }
        setBranchEzetapValid(isValid);
      }
      if (!isValid) {
        message.error(
          "This branch has no active Razorpay POS device configured. Please select another branch."
        );
        return;
      }
    }

    setSubmitting(true);

    let reference = values?.payment_reference_id;
    if (values?.payment_type === "cash") {
      reference = values?.collector;
    } else if (values?.payment_type === "credit") {
      // For credit payments, use current user as reference
      // try {
      //   const currentUser = await fetchUserDetails();
      //   reference = currentUser?.f_name || currentUser?.id?.toString() || "Current User";
      // } catch (error) {
      //   console.error("Error fetching current user:", error);
      //   reference = "Current User";
      // }
    }

    if (currentAmount > 2_00_000 && values?.payment_type === "cash") {
      if (!values?.pan_number) {
        message.error("Please enter the PAN number");
        setSubmitting(false);
        return;
      }
      await editUser(
        {
          pan: values?.pan_number,
        },
        estimateDetails?.client ?? estimateDetails?.client?.id
      );
    }

    const transaction = await createEstimateTransaction({
      estimate_id: estimateId,
      payment_method: values?.payment_type,
      payment_mode: values?.payment_mode,
      payment_medium: values?.payment_medium,
      payment_amount: values?.payment_amount,
      payment_reference: reference,
      organisation_branch_id: values?.organisation_branch_id,
    });

    form.resetFields();
    const prevTxns = transactions;
    const newTxns = [...transactions, transaction];
    setTransactions(newTxns);
    setPaymentType(undefined);
    setPosPaymentMode(undefined);

    setSubmitting(false);
    setLoading(false);
    maybePromptHoldStock(prevTxns, newTxns, values);
  };

  const totalPaid = useMemo(() => {
    return (
      transactions
        ?.filter((transaction) =>
          ["captured", "authorized", "authenticated"].includes(transaction?.status)
        )
        .reduce((acc, transaction) => {
          return acc + Number(transaction.payment_amount ?? 0);
        }, 0) / 100
    );
  }, [transactions]);

  // Wallet-adjusted pending amount. The parent passes `customPendingAmount`
  // (estimate total - paid - applicable wallet); only fall back to the raw
  // estimate figure when it isn't supplied.
  const pendingAmount = useMemo(() => {
    if (customPendingAmount !== undefined) return customPendingAmount;
    return Number(estimateDetails?.total_estimate_amount ?? 0) - Number(totalPaid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPendingAmount, estimateDetails, totalPaid]);


  const sumPaid = (txns: any[]) =>
    (txns ?? [])
      .filter((t) =>
        ["captured", "authorized", "authenticated"].includes(t?.status)
      )
      .reduce((acc, t) => acc + Number(t.payment_amount ?? 0), 0) / 100;

  const maybePromptHoldStock = (
    prevTxns: any[],
    newTxns: any[],
    valuesForFinish: any
  ) => {
    const total = Number(estimateDetails?.total_estimate_amount ?? 0);
    const prevPaid = sumPaid(prevTxns);
    const nowPaid = sumPaid(newTxns);
    const status = estimateDetails?.estimate_status;

    const cashJustPaid = nowPaid - prevPaid;
    const nowPending = Math.max(0, total - nowPaid);
    const prevPending = Math.min(total, nowPending + cashJustPaid);

    const crossedFiftyNow =
      total > 0 &&
      prevPending > total / 2 &&
      nowPending <= total / 2 &&
      nowPending > 0 &&
      HOLD_STOCK_ALLOWED_FROM.has(status);

    if (!crossedFiftyNow) {
      onFinish(valuesForFinish);
      return;
    }

    Modal.confirm({
      title: "50% Payment Received",
      content: `₹${(total - nowPending).toFixed(2)} covered of ₹${total.toFixed(
        2
      )}. Hold stock now, or continue collecting the remaining ₹${nowPending.toFixed(2)} toward Order Placed?`,
      okText: "Hold Stock",
      cancelText: "Continue",
      onOk: async () => {
        try {
          await editPI(estimateId, { estimate_status: "payment_pending" });
          const clientId =
            estimateDetails?.client?.id ?? estimateDetails?.client;
          await settleWalletTransactions({
            estimate_id: estimateId,
            client_id: clientId,
            organisation_branch_id: selectedBranch?.id ?? null,
          });
          message.success("Hold Stock marked and wallet applied");
        } catch (e) {
          console.error(e);
          message.error("Failed to mark Hold Stock");
        }
        onFinish({ ...valuesForFinish, marked_hold_stock: true });
      },
      onCancel: () => {
        onFinish(valuesForFinish);
      },
    });
  };

  const pinelabsDetailsList = async (branch_id: number) => {
    setPinelabsDetails(null)
    const res = await getPinelabsDetails(branch_id);
    if (res) {
      setPinelabsDetails(res);
    }
  };

  const ezetapDevicesList = async (branch_id: number) => {
    setEzetapDevices(null);
    const res = await getEzetapBranchDetails(branch_id);
    if (res) {
      setEzetapDevices(res);
    }
  };

  const validateBranchEzetap = async (branch_id: number) => {
    setCheckingBranchEzetap(true);
    setBranchEzetapValid(null);
    let isValid: boolean;
    if (ezetapBranchIdsCache) {
      isValid = (await ezetapBranchIdsCache).includes(branch_id);
    } else {
      const res = await getEzetapBranchDetails(branch_id);
      isValid = Array.isArray(res) ? res.length > 0 : Boolean(res?.id);
    }
    setBranchEzetapValid(isValid);
    setCheckingBranchEzetap(false);
    if (!isValid) {
      message.error(
        "This branch has no active Razorpay POS device configured. Please select another branch."
      );
    }
  };

  useEffect(() => {
    checkAndConfirmOrderPayment(estimateId as any, false).then((res) => {
      if (res) {
        message.success(
          "A payment has already been received for this order, please verify the pending amount."
        );
      }

      getEstimateTransactions(estimateId)
        .then((response) => {
          setTransactions(response);
        })
        .catch((error) => {
          console.error(error);
        });
    });

    fetchPIfromId(String(estimateId))
      .then((response) => {
        setEstimateDetails(response);
      })
      .catch((error) => {
        console.error(error);
      });

    fetchAllBranches()
      .then((res) => {
        setBranchList(res);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [estimateId]);

  const loadEzetapEnabledBranches = async () => {
    if (ezetapBranchIds || !branchList?.length) return;

    if (!ezetapBranchIdsCache) {
      const branches = branchList;
      ezetapBranchIdsCache = getEzetapEnabledBranchIds().then(async (ids) => {
        if (ids) return ids;
        // Fallback for servers without the list endpoint: probe each branch.
        const results = await Promise.all(
          branches.map(async (branch: any) => {
            const res = await getEzetapBranchDetails(branch?.id);
            const hasDevice = Array.isArray(res)
              ? res.length > 0
              : Boolean(res?.id);
            return hasDevice ? branch?.id : null;
          })
        );
        return results.filter((id) => id !== null) as number[];
      });
    }

    setCheckingBranchEzetap(true);
    try {
      setEzetapBranchIds(await ezetapBranchIdsCache);
    } catch (error) {
      ezetapBranchIdsCache = null;
      console.error(error);
    } finally {
      setCheckingBranchEzetap(false);
    }
  };

  useEffect(() => {
    if (!selectedBranch) {
      fetchBranchMembers()
        .then((res) => {
          setBranchMembers(res);
        })
        .catch((error) => {
          console.error(error);
        });
    } else {
      fetchBranchMembers({
        branch: selectedBranch,
      })
        .then((res) => {
          setBranchMembers(res);
        })
        .catch((error) => {
          console.error(error);
        });
      if (selectedBranch && paymentType === "pos") {
        pinelabsDetailsList(selectedBranch);
      }
      if (selectedBranch && paymentType === "razorpay-pos") {
        ezetapDevicesList(selectedBranch);
      }
      if (
        selectedBranch &&
        paymentType === "pos-manual" &&
        paymentMedium === "razorpay_pos"
      ) {
        validateBranchEzetap(selectedBranch);
      }
    }
  }, [selectedBranch]);

  const onValuesChange = (changedValues: any, allValues: any) => {
    // if (changedValues.payment_type) {
    //   setPaymentType(changedValues.payment_type);
    // }

    if (changedValues.payment_type) {
      form.setFieldsValue({
        payment_reference_id: "",
        payment_mode: undefined,
        payment_medium: undefined,
        razorpay_pos_payment_mode: undefined,
        ezetap_device: undefined,
        organisation_branch_id: undefined,
      });
      setPaymentType(changedValues.payment_type);
      setPaymentMedium(undefined);
      setSelectedBranch(null);
      setBranchEzetapValid(null);
      if (changedValues.payment_type === "razorpay-pos" && selectedBranch) {
        ezetapDevicesList(selectedBranch);
      }
      if (changedValues.payment_type === "pos" && selectedBranch) {
        pinelabsDetailsList(selectedBranch);
      }
    }
  };

  if (loading) {
    return (
      <div>
        <div className="d-flex align-items-center justify-content-center gap-2">
          <div>Processing payment</div>
          <Spin />
        </div>
        <Button type="default" onClick={onPosCancel} className="mt-3">
          Cancel Payment
        </Button>
      </div>
    );
  }

  return (
    <Form
      layout="vertical"
      onFinish={onSubmit}
      onValuesChange={onValuesChange}
      form={form}
    >
      <Form.Item name="payment_type" label="Payment Type" required>
        <Select
          placeholder="Select Payment Type"
          disabled={isAddCashToWallet}
          options={PaymentTypes(hasPermission("payment.read"))}
        />
      </Form.Item>
      {["razorpay", "pos-manual"].includes(paymentType as string) && (
        <Form.Item
          name="payment_mode"
          label="Payment Mode"
          required
          rules={[
            {
              required: true,
              message: "Please select a payment mode",
            },
          ]}
        >
          <Select
            placeholder="Select Payment Mode"
            options={PaymentModeOptions}
          />
        </Form.Item>
      )}
      {paymentType === "pos-manual" && (
        <Form.Item
          name="payment_medium"
          label="Payment Medium"
          required
          rules={[
            {
              required: true,
              message: "Please select a payment medium",
            },
          ]}
        >
          <Select
            placeholder="Select Payment Medium"
            options={PaymentMediumOptions}
            onChange={(value) => {
              setPaymentMedium(value);
              setSelectedBranch(null);
              setBranchEzetapValid(null);
              if (value === "razorpay_pos") {
                loadEzetapEnabledBranches();
              }
              form.setFieldsValue({
                organisation_branch_id: undefined,
                ezetap_device: undefined,
              });
            }}
          />
        </Form.Item>
      )}
      {/* <Form.Item
        name="payment_reference_type"
        label="Payment Reference Type"
        required
      >
        <Select
          placeholder="Select Payment Reference Type"
          options={PaymentReferenceTypes.filter((type) => {
            const selectedPaymentType = PaymentTypes.find(
              (type) => type.value === paymentType
            );
            return selectedPaymentType?.referenceTypes?.includes(type.value);
          })}
        />
      </Form.Item> */}
      {paymentType &&
        !["cash", "pos", "razorpay-pos"].includes(paymentType as string) && (
          <Form.Item
            name="payment_reference_id"
            label={  (paymentType === "credit" ? " Remarks/ PO Number/ Lead ID " : "Payment Reference ID")}
            rules={[
              {
                required: true,
                message: "Please enter the payment reference ID",
              },
            ]}
          >
            <Input placeholder="Enter Payment Reference ID" />
          </Form.Item>
        )}
      {paymentType === "pos" && (
        <div>
          <Form.Item
            name="pos_payment_mode"
            label="POS Payment Mode"
            rules={[
              {
                required: true,
                message: "Please select a payment mode",
              },
            ]}
          >
            <Select
              placeholder="Select Payment Mode"
              options={POSPaymentModes}
              onChange={(value) => {
                setPosPaymentMode(value);
                form.setFieldsValue({
                  payment_reference_id: "",
                });
              }}
            />
          </Form.Item>
        </div>
      )}
      {paymentType === "razorpay-pos" && (
        <Form.Item
          name="razorpay_pos_payment_mode"
          label="Payment Mode"
          rules={[
            {
              required: true,
              message: "Please select a payment mode",
            },
          ]}
        >
          <Select
            placeholder="Select Payment Mode"
            options={RazorpayPOSPaymentModes}
          />
        </Form.Item>
      )}
      {!isAddCashToWallet &&
        paymentType !== "credit" &&
        (paymentType !== "pos-manual" || paymentMedium === "razorpay_pos") && (
        <Form.Item
          name="organisation_branch_id"
          label="Branch"
          required
          validateStatus={branchEzetapValid === false ? "error" : undefined}
          help={
            branchEzetapValid === false
              ? "No active Razorpay POS device configured for this branch"
              : undefined
          }
        >
          <Select
            placeholder="Select Branch"
            options={branchList
              ?.filter((branch) =>
                paymentType === "pos-manual" &&
                paymentMedium === "razorpay_pos" &&
                ezetapBranchIds
                  ? ezetapBranchIds.includes(branch?.id)
                  : true
              )
              ?.map((branch) => ({
                label: branch?.branch_name,
                value: branch?.id,
              }))}
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            optionFilterProp="label"
            className="w-100"
            onChange={(value) => {
              setSelectedBranch(value);
              setBranchEzetapValid(null);
              form.setFieldsValue({
                pinelab_machine_name: undefined,
                ezetap_device: undefined,
              });
            }}
          />
        </Form.Item>
      )}
      {paymentType === "pos" && selectedBranch && (
        <div>
          <Form.Item
            name="pinelab_machine_name"
            label="Pinelab Machine Name"
            rules={[
              {
                required: true,
                message: "Please select a Pinelab Machine Name",
              },
            ]}
          >
            <Select
              placeholder="Select Pinelab Machine Name"
              options={pinelabsDetails?.map((machine: any) => ({
                label: machine?.machine_name,
                value: machine?.machine_id,
              }))}
            />
          </Form.Item>
        </div>
      )}
      {paymentType === "razorpay-pos" && selectedBranch && (
        <Form.Item
          name="ezetap_device"
          label="Razorpay POS Device"
          rules={[
            {
              required: true,
              message: "Please select a Razorpay POS device",
            },
          ]}
        >
          <Select
            placeholder="Select Razorpay POS Device"
            options={ezetapDevices?.map((device: any) => ({
              label: device?.device_name
                ? `${device.device_name} (${device.device_serial})`
                : device.device_serial,
              value: device?.id,
            }))}
          />
        </Form.Item>
      )}
      {paymentType === "cash" && (
        <Form.Item
          name="collector"
          label="Collected By"
          rules={[
            {
              required: true,
              message: "Please enter the name of the collector",
            },
          ]}
        >
          <Select
            placeholder="Select Collector"
            options={allbranchMembers
              ?.filter((member) => [1, 2].includes(member?.user_permission))
              ?.map((member) => ({
                value: member.user?.id,
                label: member.user?.f_name,
              }))}
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            optionFilterProp="label"
            className="w-100"
          ></Select>
        </Form.Item>
      )}
      {isAddCashToWallet && (
        <Form.Item
          name="wallet_collected_by"
          label="Collected By"
          rules={[{ required: true, message: "Please select who collected the cash" }]}
        >
          <Select
            placeholder="Select Collector"
            options={allbranchMembers?.map((member) => ({
              value: member.user?.id,
              label: member.user?.f_name,
            }))}
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            optionFilterProp="label"
            className="w-100"
          />
        </Form.Item>
      )}
      {!isAddCashToWallet && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <div className="fw-bold">Amount Pending</div>
            <div>
              ₹{" "}
              {pendingAmount.toFixed(2)}
            </div>
          </div>
          <div>
            <div className="fw-bold">Total Paid</div>
            <div>₹ {totalPaid}</div>
          </div>
        </div>
      )}
      <Form.Item
        label="Amount"
        name="payment_amount"
        required
        rules={[
          // Cannot be more than the amount pending
          {
            validator: async (_, value) => {
              if (value === null || value === undefined || value === "" || Number(value) <= 0) {
                return Promise.reject(new Error("Please enter a valid amount"));
              }
              // if (
              //   Number(value) >
              //   Number(estimateDetails?.total_estimate_amount) -
              //   Number(totalPaid)
              // ) {
              //   return Promise.reject(
              //     new Error("Amount cannot be more than the amount pending")
              //   );
              // }
              return Promise.resolve();
            },
          },
        ]}
      >
        <InputNumber
          className="w-100"
          placeholder="Enter Amount"
          min={0}
          onChange={(n) => {
            setCurrentAmount(n || 0);
          }}
        />
      </Form.Item>
      {currentAmount > 2_00_000 && paymentType === "cash" && (
        <Form.Item
          name="pan_number"
          label="PAN Number"
          rules={[
            {
              required: true,
              message: "Please enter the PAN number",
            },
            {
              pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
              message: "Please enter a valid PAN number",
            },
          ]}
        >
          <Input placeholder="Enter PAN Number" />
        </Form.Item>
      )}
      <Form.Item className="d-flex justify-content-end">
        <Space>
          <Button type="default" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={submitting} disabled={!hasPermission("pi.mark_as_order_placed")}>
            Submit
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}
